import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ArrowsAiDocumentsAPI from 'dashboard/api/arrowsAi/documents';
import {
  fromApi,
  isProcessing,
} from 'dashboard/composables/arrowsAi/documentMapper';
import {
  parseAPIErrorResponse,
  throwErrorMessage,
} from 'dashboard/store/utils/api';

export const MAX_FILES_PER_BATCH = 10;
const POLL_INTERVAL = 2000;

export function useArrowsAiDocuments(assistantId) {
  const { t } = useI18n();
  const documents = ref([]);
  const isLoading = ref(false);
  let loadedAssistantId = null;
  let pollTimer = null;

  const stopPolling = () => {
    if (!pollTimer) return;
    clearInterval(pollTimer);
    pollTimer = null;
  };

  const fetchDocuments = async (id, { force, silent } = {}) => {
    if (!id) {
      documents.value = [];
      loadedAssistantId = null;
      stopPolling();
      return;
    }
    if (!force && loadedAssistantId === id) return;

    if (!silent) isLoading.value = true;
    try {
      const { data } = await ArrowsAiDocumentsAPI.get({ assistantId: id });
      documents.value = (Array.isArray(data) ? data : []).map(fromApi);
      loadedAssistantId = id;
    } catch (error) {
      if (silent) return;
      documents.value = [];
      loadedAssistantId = id;
      const message = parseAPIErrorResponse(error);
      useAlert(
        typeof message === 'string'
          ? message
          : t('ARROWS_AI.DOCUMENTS.FETCH_ERROR')
      );
    } finally {
      if (!silent) isLoading.value = false;
    }
  };

  const syncPolling = () => {
    const busy = documents.value.some(isProcessing);
    if (busy && !pollTimer) {
      pollTimer = setInterval(() => {
        fetchDocuments(assistantId.value, { force: true, silent: true });
      }, POLL_INTERVAL);
    }
    if (!busy) stopPolling();
  };

  watch(assistantId, id => fetchDocuments(id), { immediate: true });
  watch(documents, syncPolling, { deep: true });
  onUnmounted(stopPolling);

  const addDocuments = async files => {
    try {
      const { data } = await ArrowsAiDocumentsAPI.create({
        assistantId: assistantId.value,
        files,
      });
      const created = (Array.isArray(data) ? data : []).map(fromApi);
      documents.value = [...created, ...documents.value];
      return created;
    } catch (error) {
      return throwErrorMessage(error);
    }
  };

  const deleteDocuments = async ids => {
    const uniqueIds = [...new Set(ids.map(Number))];
    try {
      await Promise.all(
        uniqueIds.map(id =>
          ArrowsAiDocumentsAPI.delete({ assistantId: assistantId.value, id })
        )
      );
      const idSet = new Set(uniqueIds);
      documents.value = documents.value.filter(item => !idSet.has(item.id));
    } catch (error) {
      await fetchDocuments(assistantId.value, { force: true });
      throwErrorMessage(error);
    }
  };

  const downloadDocument = async document => {
    const { data } = await ArrowsAiDocumentsAPI.download({
      assistantId: assistantId.value,
      id: document.id,
    });
    const url = URL.createObjectURL(data);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = document.fileName || 'document';
    window.document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const documentContent = async id => {
    const { data } = await ArrowsAiDocumentsAPI.content({
      assistantId: assistantId.value,
      id,
    });
    return data.content || '';
  };

  const saveDocumentText = async (id, content) => {
    const { data } = await ArrowsAiDocumentsAPI.updateContent({
      assistantId: assistantId.value,
      id,
      content,
    });
    const updated = fromApi(data);
    documents.value = documents.value.map(item =>
      item.id === updated.id ? updated : item
    );
    return updated;
  };

  return {
    documents: computed(() => documents.value),
    isLoading: computed(() => isLoading.value),
    addDocuments,
    deleteDocuments,
    downloadDocument,
    documentContent,
    saveDocumentText,
  };
}
